/**
 * Global Form Configuration
 *
 * Centralized configuration for form styling, validation, and interactions across the application.
 * This ensures consistent form behavior and appearance throughout all forms.
 */

import type { CSSProperties as ReactCSSProperties } from 'react';

// Allow CSS-in-JS pseudo-selectors and nested rules (e.g. ':hover', '::after')
// React's CSSProperties is strict about valid CSS keys only.
type CSSProperties = ReactCSSProperties & Record<string, any>;

export interface FormConfig {
  /** Form container styles */
  container: {
    default: CSSProperties;
    inline: CSSProperties;
    stacked: CSSProperties;
  };
  /** Field configurations */
  field: {
    /** Field wrapper styles */
    wrapper: CSSProperties;
    /** Label styles */
    label: {
      default: CSSProperties;
      required: CSSProperties;
      optional: CSSProperties;
      error: CSSProperties;
    };
    /** Input styles */
    input: {
      default: CSSProperties;
      hover: CSSProperties;
      focus: CSSProperties;
      error: CSSProperties;
      disabled: CSSProperties;
      success: CSSProperties;
    };
    /** Textarea styles */
    textarea: {
      default: CSSProperties;
      focus: CSSProperties;
      error: CSSProperties;
    };
    /** Select/dropdown styles */
    select: {
      default: CSSProperties;
      focus: CSSProperties;
      error: CSSProperties;
    };
    /** Checkbox and radio styles */
    checkbox: {
      wrapper: CSSProperties;
      input: CSSProperties;
      label: CSSProperties;
    };
    /** Help text styles */
    helpText: {
      default: CSSProperties;
      error: CSSProperties;
      warning: CSSProperties;
      success: CSSProperties;
    };
  };
  /** Button configurations */
  button: {
    primary: CSSProperties;
    secondary: CSSProperties;
    danger: CSSProperties;
    ghost: CSSProperties;
    disabled: CSSProperties;
    loading: CSSProperties;
  };
  /** Validation configurations */
  validation: {
    /** Error message styles */
    errorMessage: CSSProperties;
    /** Success message styles */
    successMessage: CSSProperties;
    /** Warning message styles */
    warningMessage: CSSProperties;
    /** Field validation indicators */
    indicators: {
      required: string;
      optional: string;
      error: string;
      success: string;
    };
  };
  /** Layout configurations */
  layout: {
    /** Field spacing */
    fieldSpacing: string;
    /** Section spacing */
    sectionSpacing: string;
    /** Form padding */
    formPadding: string;
    /** Button group spacing */
    buttonSpacing: string;
  };
  /** Field grouping configurations */
  fieldGroup: {
    /** Field group container styles */
    container: CSSProperties;
    /** Field group title styles */
    title: CSSProperties;
    /** Field group description styles */
    description: CSSProperties;
    /** Spacing between field groups */
    groupSpacing: string;
    /** Spacing within a field group */
    fieldSpacing: string;
  };
  /** Animation configurations */
  animation: {
    /** Focus transition */
    focusDuration: number;
    /** Error shake duration */
    errorShakeDuration: number;
    /** Success feedback duration */
    successDuration: number;
  };
}

export const formConfig: FormConfig = {
  // Form container styles
  container: {
    default: {
      width: '100%',
      maxWidth: '800px', // Increased from 600px
    },
    inline: {
      display: 'flex',
      flexDirection: 'row',
      gap: '16px',
      alignItems: 'flex-end',
    },
    stacked: {
      display: 'flex',
      flexDirection: 'column',
      gap: '24px',
    },
  },

  // Field configurations
  field: {
    wrapper: {
      marginBottom: '20px',
      width: '100%',
    },
    label: {
      default: {
        display: 'block',
        fontSize: '14px',
        fontWeight: '500',
        color: 'hsl(var(--foreground))',
        marginBottom: '6px',
      },
      required: {
        color: 'hsl(var(--foreground))',
        '::after': {
          content: '" *"',
          color: 'hsl(var(--destructive))',
          marginLeft: '2px',
        },
      },
      optional: {
        color: 'hsl(var(--muted-foreground))',
        fontSize: '13px',
      },
      error: {
        color: 'hsl(var(--destructive))',
      },
    },
    input: {
      default: {
        width: '100%',
        padding: '10px 12px',
        fontSize: '14px',
        lineHeight: '20px',
        color: 'hsl(var(--foreground))',
        backgroundColor: 'transparent',
        borderWidth: '2px',
        borderStyle: 'solid',
        borderColor: 'hsl(var(--border))',
        borderRadius: '8px',
        outline: 'none',
        transition: 'all 0.2s ease',
      },
      hover: {
        borderColor: 'hsl(var(--input))',
        backgroundColor: 'transparent',
      },
      focus: {
        borderColor: 'hsl(var(--ring))',
        backgroundColor: 'transparent',
        boxShadow: '0 0 0 3px hsl(var(--ring) / 0.15)',
      },
      error: {
        borderColor: 'hsl(var(--destructive))',
        backgroundColor: 'transparent',
        color: 'hsl(var(--foreground))',
      },
      disabled: {
        backgroundColor: 'hsl(var(--muted))',
        color: 'hsl(var(--muted-foreground))',
        cursor: 'not-allowed',
        opacity: 0.6,
      },
      success: {
        borderColor: 'hsl(142.1 76.2% 36.3%)',
        backgroundColor: 'transparent',
      },
    },
    textarea: {
      default: {
        width: '100%',
        padding: '10px 12px',
        fontSize: '14px',
        lineHeight: '20px',
        color: 'hsl(var(--foreground))',
        backgroundColor: 'transparent',
        borderWidth: '2px',
        borderStyle: 'solid',
        borderColor: 'hsl(var(--border))',
        borderRadius: '8px',
        outline: 'none',
        transition: 'all 0.2s ease',
        minHeight: '100px',
        resize: 'vertical',
      },
      focus: {
        borderColor: 'hsl(var(--ring))',
        boxShadow: '0 0 0 3px hsl(var(--ring) / 0.15)',
      },
      error: {
        borderColor: 'hsl(var(--destructive))',
        backgroundColor: 'transparent',
      },
    },
    select: {
      default: {
        width: '100%',
        padding: '10px 12px',
        fontSize: '14px',
        lineHeight: '20px',
        color: 'hsl(var(--foreground))',
        backgroundColor: 'transparent',
        borderWidth: '2px',
        borderStyle: 'solid',
        borderColor: 'hsl(var(--border))',
        borderRadius: '8px',
        outline: 'none',
        transition: 'all 0.2s ease',
        cursor: 'pointer',
      },
      focus: {
        borderColor: 'hsl(var(--ring))',
        boxShadow: '0 0 0 3px hsl(var(--ring) / 0.15)',
      },
      error: {
        borderColor: 'hsl(var(--destructive))',
        backgroundColor: 'transparent',
      },
    },
    checkbox: {
      wrapper: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      },
      input: {
        width: '18px',
        height: '18px',
        borderRadius: '4px',
        borderWidth: '2px',
        borderStyle: 'solid',
        borderColor: 'hsl(var(--border))',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      },
      label: {
        fontSize: '14px',
        color: 'hsl(var(--foreground))',
        cursor: 'pointer',
        userSelect: 'none',
      },
    },
    helpText: {
      default: {
        fontSize: '12px',
        color: 'hsl(var(--muted-foreground))',
        marginTop: '4px',
        lineHeight: '16px',
      },
      error: {
        fontSize: '12px',
        color: 'hsl(var(--destructive))',
        marginTop: '4px',
        lineHeight: '16px',
        fontWeight: '500',
      },
      warning: {
        fontSize: '12px',
        color: 'hsl(33 100% 50%)',
        marginTop: '4px',
        lineHeight: '16px',
      },
      success: {
        fontSize: '12px',
        color: 'hsl(142.1 76.2% 36.3%)',
        marginTop: '4px',
        lineHeight: '16px',
      },
    },
  },

  // Button configurations
  button: {
    primary: {
      padding: '10px 20px',
      fontSize: '14px',
      fontWeight: '500',
      color: 'hsl(var(--primary-foreground))',
      backgroundColor: 'hsl(var(--primary))',
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: 'hsl(var(--primary))',
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      outline: 'none',
      ':hover': {
        backgroundColor: 'hsl(var(--primary) / 0.9)',
        borderColor: 'hsl(var(--primary) / 0.9)',
        transform: 'translateY(-1px)',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      },
      ':active': {
        transform: 'translateY(0)',
      },
    },
    secondary: {
      padding: '10px 20px',
      fontSize: '14px',
      fontWeight: '500',
      color: 'hsl(var(--foreground))',
      backgroundColor: 'transparent',
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: 'hsl(var(--border))',
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      outline: 'none',
      ':hover': {
        backgroundColor: 'hsl(var(--muted))',
        borderColor: 'hsl(var(--border))',
      },
    },
    danger: {
      padding: '10px 20px',
      fontSize: '14px',
      fontWeight: '500',
      color: 'hsl(var(--primary-foreground))',
      backgroundColor: 'hsl(var(--destructive))',
      border: '2px solid hsl(var(--destructive))',
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      outline: 'none',
      ':hover': {
        backgroundColor: 'hsl(var(--destructive) / 0.9)',
        borderColor: 'hsl(var(--destructive) / 0.9)',
      },
    },
    ghost: {
      padding: '10px 20px',
      fontSize: '14px',
      fontWeight: '500',
      color: 'hsl(var(--primary))',
      backgroundColor: 'transparent',
      border: '2px solid transparent',
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      outline: 'none',
      ':hover': {
        backgroundColor: 'hsl(var(--accent))',
      },
    },
    disabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
      ':hover': {
        transform: 'none',
        boxShadow: 'none',
      },
    },
    loading: {
      position: 'relative',
      color: 'transparent',
      pointerEvents: 'none',
      '::after': {
        content: '""',
        position: 'absolute',
        width: '16px',
        height: '16px',
        top: '50%',
        left: '50%',
        marginLeft: '-8px',
        marginTop: '-8px',
        border: '2px solid hsl(var(--primary-foreground))',
        borderTopColor: 'transparent',
        borderRadius: '50%',
        animation: 'spin 0.6s linear infinite',
      },
    },
  },

  // Validation configurations
  validation: {
    errorMessage: {
      color: 'hsl(var(--destructive))',
      fontSize: '12px',
      marginTop: '4px',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
    },
    successMessage: {
      color: 'hsl(142.1 76.2% 36.3%)',
      fontSize: '12px',
      marginTop: '4px',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
    },
    warningMessage: {
      color: 'hsl(33 100% 50%)',
      fontSize: '12px',
      marginTop: '4px',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
    },
    indicators: {
      required: '*',
      optional: '(optional)',
      error: '⚠',
      success: '✓',
    },
  },

  // Layout configurations
  layout: {
    fieldSpacing: '20px',
    sectionSpacing: '32px',
    formPadding: '24px',
    buttonSpacing: '12px',
  },

  // Field grouping configurations
  fieldGroup: {
    container: {
      marginBottom: '32px',
      padding: '24px',
      backgroundColor: 'hsl(var(--muted))',
      border: '1px solid hsl(var(--border))',
      borderRadius: '12px',
      position: 'relative',
    },
    title: {
      fontSize: '16px',
      fontWeight: '600',
      color: 'hsl(var(--foreground))',
      marginBottom: '8px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    description: {
      fontSize: '14px',
      color: 'hsl(var(--muted-foreground))',
      marginBottom: '20px',
      lineHeight: '1.5',
    },
    groupSpacing: '32px',
    fieldSpacing: '16px',
  },

  // Animation configurations
  animation: {
    focusDuration: 200,
    errorShakeDuration: 300,
    successDuration: 2000,
  },
};

/**
 * Utility function to get input styles based on state
 */
export function getInputStyles(
  hasError: boolean = false,
  isFocused: boolean = false,
  isDisabled: boolean = false,
  hasSuccess: boolean = false
): CSSProperties {
  const styles = { ...formConfig.field.input.default };

  if (isDisabled) {
    Object.assign(styles, formConfig.field.input.disabled);
  } else if (hasError) {
    Object.assign(styles, formConfig.field.input.error);
  } else if (hasSuccess) {
    Object.assign(styles, formConfig.field.input.success);
  } else if (isFocused) {
    Object.assign(styles, formConfig.field.input.focus);
  }

  return styles;
}

/**
 * Utility function to get button styles based on variant
 */
export function getButtonStyles(
  variant: 'primary' | 'secondary' | 'danger' | 'ghost' = 'primary',
  isDisabled: boolean = false,
  isLoading: boolean = false
): CSSProperties {
  const styles = { ...formConfig.button[variant] };

  if (isDisabled) {
    Object.assign(styles, formConfig.button.disabled);
  }

  if (isLoading) {
    Object.assign(styles, formConfig.button.loading);
  }

  return styles;
}

/**
 * CSS class names for form styling
 */
export const formClasses = {
  // Container classes
  container: 'app-form',
  containerInline: 'app-form--inline',
  containerStacked: 'app-form--stacked',
  containerShake: 'app-form--shake',
  
  // Field classes
  field: 'app-form-field',
  fieldError: 'app-form-field--error',
  fieldSuccess: 'app-form-field--success',
  fieldShake: 'app-form-field--shake',
  
  // Label classes
  label: 'app-form-label',
  labelRequired: 'app-form-label--required',
  labelOptional: 'app-form-label--optional',
  
  // Input classes
  input: 'app-form-input',
  inputError: 'app-form-input--error',
  inputSuccess: 'app-form-input--success',
  inputDisabled: 'app-form-input--disabled',
  inputShake: 'app-form-input--shake',
  
  // Textarea classes
  textarea: 'app-form-textarea',
  textareaError: 'app-form-textarea--error',
  
  // Select classes
  select: 'app-form-select',
  selectError: 'app-form-select--error',
  
  // Checkbox classes
  checkbox: 'app-form-checkbox',
  checkboxWrapper: 'app-form-checkbox-wrapper',
  checkboxLabel: 'app-form-checkbox-label',
  
  // Button classes
  button: 'app-form-button',
  buttonPrimary: 'app-form-button--primary',
  buttonSecondary: 'app-form-button--secondary',
  buttonDanger: 'app-form-button--danger',
  buttonGhost: 'app-form-button--ghost',
  buttonDisabled: 'app-form-button--disabled',
  buttonLoading: 'app-form-button--loading',
  
  // Help text classes
  helpText: 'app-form-help',
  helpTextError: 'app-form-help--error',
  helpTextWarning: 'app-form-help--warning',
  helpTextSuccess: 'app-form-help--success',
  
  // Message classes
  errorMessage: 'app-form-error',
  successMessage: 'app-form-success',
  warningMessage: 'app-form-warning',
  
  // Field group classes
  fieldGroup: 'app-form-field-group',
  fieldGroupTitle: 'app-form-field-group-title',
  fieldGroupDescription: 'app-form-field-group-description',
  fieldGroupContent: 'app-form-field-group-content',
  
  // Form classes
  form: 'app-form',
  formStacked: 'app-form--stacked',
  formInline: 'app-form--inline',
  formShake: 'app-form--shake',
  
  // Error class
  error: 'app-form-error',
} as const;

/**
 * Form field validation patterns
 */
export const validationPatterns = {
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  phone: /^\+?[\d\s\-\(\)]+$/,
  url: /^https?:\/\/.+\..+$/,
  alphanumeric: /^[a-zA-Z0-9]+$/,
  numeric: /^\d+$/,
  decimal: /^\d+(\.\d+)?$/,
  slug: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
} as const;

/**
 * Default form validation messages
 */
export const validationMessages = {
  required: 'This field is required',
  email: 'Please enter a valid email address',
  phone: 'Please enter a valid phone number',
  url: 'Please enter a valid URL',
  minLength: (min: number) => `Must be at least ${min} characters`,
  maxLength: (max: number) => `Must be no more than ${max} characters`,
  min: (min: number) => `Must be at least ${min}`,
  max: (max: number) => `Must be no more than ${max}`,
  pattern: 'Please enter a valid value',
} as const;

/**
 * Utility function to get field group styles
 */
export function getFieldGroupStyles(
  variant: 'default' | 'compact' | 'bordered' | 'card' = 'default'
): CSSProperties {
  const baseStyles = { ...formConfig.fieldGroup.container };
  
  switch (variant) {
    case 'compact':
      return {
        ...baseStyles,
        padding: '16px',
        marginBottom: '20px',
        backgroundColor: 'transparent',
        border: '1px solid hsl(var(--border))',
      };
    case 'bordered':
      return {
        ...baseStyles,
        backgroundColor: 'hsl(var(--card))',
        border: '2px solid hsl(var(--border))',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
      };
    case 'card':
      return {
        ...baseStyles,
        backgroundColor: 'hsl(var(--card))',
        border: '1px solid hsl(var(--border))',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        borderRadius: '16px',
        padding: '32px',
      };
    default:
      return baseStyles;
  }
}

/**
 * Phone Number Configuration
 * 
 * DEFAULT: US Phone Numbers
 * - Format: (XXX) XXX-XXXX
 * - Max digits: 10 (area code + number)
 * - Auto-formatting: Enabled
 * - Validation: US pattern (10 digits or 11 with country code 1)
 * 
 * INTERNATIONAL SUPPORT:
 * To enable international phone numbers, update the following:
 * 1. Remove the 10-digit limit in handleFieldChange()
 * 2. Update formatPhoneNumber() to handle international formats
 * 3. Modify validationPatterns.phone to accept international patterns
 * 4. Update placeholder text to show international example
 * 5. Consider using a library like libphonenumber-js for robust international support
 * 
 * Example international configuration:
 * - UK: +44 XXXX XXXXXX (11-12 digits)
 * - Germany: +49 XXX XXXXXXXX (11-12 digits)
 * - Japan: +81 XX XXXX XXXX (11 digits)
 * - Australia: +61 X XXXX XXXX (11 digits)
 */
export const phoneNumberConfig = {
  defaultCountry: 'US',
  maxDigits: 10,
  format: '(XXX) XXX-XXXX',
  countryCode: '+1',
  placeholder: '(555) 123-4567',
  validation: {
    minDigits: 10,
    maxDigits: 10,
    allowCountryCode: true,
    acceptedCountryCodes: ['1'], // US and Canada
  },
  formatting: {
    autoFormat: true,
    formatOnBlur: true,
    formatOnType: true,
  },
} as const;

/**
 * Utility function to trigger shake animation on form or field
 */
export function triggerShakeAnimation(element: HTMLElement | null, type: 'form' | 'field' | 'input' = 'field'): void {
  if (!element) return;
  
  const shakeClass = `app-form${type === 'form' ? '' : type === 'field' ? '-field' : '-input'}--shake`;
  
  // Remove existing animation class if present
  element.classList.remove(shakeClass);
  
  // Trigger reflow to ensure class removal takes effect
  element.offsetHeight;
  
  // Add shake animation class
  element.classList.add(shakeClass);
  
  // Remove animation class after animation completes
  const animationDuration = type === 'form' ? 500 : 400;
  setTimeout(() => {
    element.classList.remove(shakeClass);
  }, animationDuration);
}

/**
 * Utility function to shake invalid form fields
 */
export function shakeInvalidFields(formElement: HTMLElement | null, errors: Record<string, string>): void {
  if (!formElement) return;
  
  Object.keys(errors).forEach(fieldName => {
    if (errors[fieldName]) { // Only shake fields with actual error messages
      const fieldElement = formElement.querySelector(`[name="${fieldName}"]`) as HTMLElement;
      if (fieldElement) {
        triggerShakeAnimation(fieldElement, 'input');
      }
    }
  });
}